// src/components/admin/bespokeai/EventsAI.jsx
import React, { useState, useRef, useEffect } from 'react';
import { auth } from '../../../utils/firebase';
import { fetchEventsContext } from '../../../api/aiContextApi';
import DailyTokenCounter from './DailyTokenCounter';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const PRESET_PROMPTS = [
  'Write an event description for a support group session',
  'Draft a volunteer briefing for event day',
  'Suggest programme ideas for widows returning to work',
  'Create a post-event thank you message',
  'Plan a community outing for 30 members',
];

const INITIAL_MESSAGE = {
  id: 'init',
  role: 'assistant',
  type: 'ai',
  content: "Hello! I'm BespokeAI, your intelligent assistant for events and programmes. I can help you write event descriptions, volunteer briefings, post-event messages, and programme planning ideas. How can I help you today?",
};

export default function EventsAI({ orgId, adminId }) {
  const [messages, setMessages] = useState([INITIAL_MESSAGE]);
  const [input,    setInput]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const counterRef = useRef(null);
  const [copied,   setCopied]   = useState(null);
  const chatRef    = useRef(null);
  const prevLenRef = useRef(1);

  useEffect(() => {
    const prev = prevLenRef.current;
    prevLenRef.current = messages.length;
    if (messages.length > prev && prev > 0 && chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async (text) => {
    const trimmed = text.trim();
    if (!trimmed || loading || limitReached) return;

    const updatedMessages = [...messages, { id: Date.now(), role: 'user', type: 'user', content: trimmed }];
    setMessages(updatedMessages);
    setInput('');
    setLoading(true);

    try {
      const [token, context] = await Promise.all([
        auth.currentUser.getIdToken(),
        fetchEventsContext(orgId),
      ]);

      const apiMessages = updatedMessages
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .map(m => ({ role: m.role, content: m.content }));

      const res = await fetch(`${API_URL}/api/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ messages: apiMessages, module: 'events', context }),
      });
      const data = await res.json();
      if (data.dailyUsage) counterRef.current?.update(data.dailyUsage);
      const reply = data.success ? data.text : (data.message || 'Something went wrong. Please try again.');

      setMessages(prev => [...prev, { id: Date.now() + 1, role: 'assistant', type: 'ai', content: reply, showCopy: true }]);
    } catch (err) {
      setMessages(prev => [...prev, { id: Date.now() + 1, role: 'assistant', type: 'ai', content: `Error: ${err.message}`, showCopy: false }]);
    }
    setLoading(false);
  };

  const handleSend  = () => sendMessage(input);
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleCopy = (id, content) => {
    navigator.clipboard.writeText(content).then(() => {
      setCopied(id);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  return (
    <div className="bb-ai-container">
      <div className="bb-ai-header">
        <h3>BespokeAI — Events</h3>
        <p>AI-powered event descriptions, briefings, and programme ideas</p>
      </div>

      <DailyTokenCounter ref={counterRef} orgId={orgId} onLimitReached={setLimitReached} />

      <div className="bb-ai-messages" ref={chatRef}>
        {messages.map(msg => (
          <div key={msg.id} className={`bb-ai-msg bb-ai-msg-${msg.type}`}>
            <div className={`bb-ai-bubble-${msg.type === 'user' ? 'user' : 'ai'}`}>
              <pre>{msg.content}</pre>
            </div>
            {msg.type === 'ai' && msg.showCopy && (
              <div className="bb-ai-actions">
                <button className="bb-ai-action-btn" onClick={() => handleCopy(msg.id, msg.content)}>
                  {copied === msg.id ? 'Copied!' : 'Copy Text'}
                </button>
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div className="bb-ai-msg bb-ai-msg-ai">
            <div className="bb-ai-bubble-ai" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{ opacity: 0.5 }}>● ● ●</span>
            </div>
          </div>
        )}
      </div>

      <div className="bb-ai-presets">
        {PRESET_PROMPTS.map((p, i) => (
          <button key={i} className="bb-ai-preset-chip" onClick={() => sendMessage(p)} disabled={loading || limitReached}>
            {p}
          </button>
        ))}
      </div>

      <div className="bb-ai-input-row">
        <textarea
          className="bb-ai-input"
          placeholder={limitReached ? 'Daily AI usage limit reached. Resets at 00:00:01.' : "Ask BespokeAI about your events or programmes..."}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={limitReached}
          rows={1}
        />
        <button className="bb-ai-send-btn" onClick={handleSend} disabled={loading || !input.trim() || limitReached}>
          Send
        </button>
      </div>
    </div>
  );
}
